import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Clock, MapPin, MessageCircle, Sparkles } from "lucide-react";
import { Field, PrimaryBtn, inputCls } from "@/components/Common";
import { BookingDialog } from "@/components/BookingDialog";
import { WHATSAPP, http, inr, today } from "@/lib/api";

export default function PackageDetail() {
  const { slug } = useParams();
  const [date, setDate] = useState(today(14));
  const [pax, setPax] = useState(2);
  const [open, setOpen] = useState(false);
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["package", slug], queryFn: () => http.get(`/packages/${slug}`).then((r) => r.data) });

  if (isLoading) return <div className="h-[60vh] animate-pulse bg-[#F4F0EA]" />;
  if (isError) return <p className="p-20 text-center" data-testid="package-not-found">Package not found.</p>;

  return (
    <div data-testid="package-detail-page">
      <section className="relative h-[70vh] min-h-[460px] overflow-hidden grain">
        <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-slate-950/20" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-14 sm:px-8 fade-up">
          <Link to="/packages" className="mb-auto mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm text-white backdrop-blur" data-testid="package-back-link"><ArrowLeft className="h-4 w-4" />All packages</Link>
          <p className="eyebrow !text-[#F4A261]">{p.region === "domestic" ? "India" : p.country}</p>
          <h1 className="font-display mt-2 text-4xl font-bold text-white sm:text-5xl lg:text-6xl" data-testid="package-name">{p.name}</h1>
          <div className="mt-4 flex flex-wrap gap-5 text-sm text-slate-200">
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{p.location}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{p.days} days / {p.days - 1} nights</span>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-8 lg:grid-cols-[1fr_380px]">
        <div>
          <p className="text-lg leading-relaxed text-slate-700">{p.summary}</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {p.highlights.map((h) => <p key={h} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-[#E07A5F]" />{h}</p>)}
          </div>
          <h2 className="font-display mt-14 text-4xl font-bold text-[#0D1B2A]">Day-by-day itinerary</h2>
          <ol className="mt-8 border-l border-[#E5E0D8]" data-testid="package-itinerary">
            {p.itinerary.map((d) => (
              <li key={d.day} className="relative pb-8 pl-8">
                <span className="absolute -left-[13px] top-0 grid h-6 w-6 place-items-center rounded-full bg-[#0D1B2A] text-[11px] font-bold text-white">{d.day}</span>
                <h3 className="font-semibold text-[#0D1B2A]">{d.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{d.description}</p>
              </li>
            ))}
          </ol>
          <Link to={`/planner?destination=${encodeURIComponent(p.location)}&days=${p.days}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#C2603F]" data-testid="package-customise-link">
            <Sparkles className="h-4 w-4" />Customise this trip with the AI planner
          </Link>
        </div>

        <aside className="h-fit rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm lg:sticky lg:top-24" data-testid="package-booking-card">
          <p className="text-xs uppercase tracking-wider text-slate-500">Price per person</p>
          <p className="font-display text-4xl font-bold text-[#0D1B2A]">{inr(p.price)}</p>
          <div className="mt-6 space-y-3">
            <Field label="Start date"><input type="date" min={today()} className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="package-date-input" /></Field>
            <Field label="Travellers"><input type="number" min={1} max={20} className={inputCls} value={pax} onChange={(e) => setPax(Math.max(1, +e.target.value))} data-testid="package-pax-input" /></Field>
          </div>
          <div className="mt-5 flex justify-between border-t border-[#E5E0D8] pt-4 font-semibold">
            <span>Total</span><span className="text-[#C2603F]" data-testid="package-total">{inr(p.price * pax)}</span>
          </div>
          <PrimaryBtn className="mt-5 w-full" onClick={() => setOpen(true)} data-testid="package-book-btn">Book this holiday</PrimaryBtn>
          <a href={`${WHATSAPP.link}?text=${encodeURIComponent(`Hi NextVisionSolutions, I'm interested in the "${p.name}" package (${p.days} days, ${p.location}) starting ${date} for ${pax} traveller(s). Please share details. ${window.location.href}`)}`}
            target="_blank" rel="noreferrer" data-testid="package-whatsapp-btn"
            className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full border-2 border-[#25D366] text-sm font-semibold text-[#128C7E] transition-colors hover:bg-[#25D366] hover:text-white">
            <MessageCircle className="h-4 w-4" />Ask on WhatsApp
          </a>
          <p className="mt-3 text-center text-xs text-slate-500">Hotels, transfers & sightseeing included. Flights from India where mentioned.</p>
        </aside>
      </div>
      <BookingDialog open={open} onOpenChange={setOpen} title={p.name} amount={p.price * pax}
        lines={[["Duration", `${p.days} days`], ["Start date", date], ["Travellers", pax]]}
        booking={{ type: "package", item_id: p.id, travel_date: date, travellers: pax }} />
    </div>
  );
}
